/**
 * チバリヨ - S1 ホーム (Phase 2-B-3.1)
 *
 * 「今日の動きを5秒で把握する」画面。
 *
 * セクション構成 (上から):
 *   1. アラート (自分に関するものだけ・該当なしなら非表示)
 *      - 日報未入力: 最終日報が alertNoReportDays 日より前 (KPI 対象者のみ)
 *      - ペース危険: ペース pt が alertPacePtThreshold 以下 (KPI 対象者のみ・月初判定スキップ)
 *   2. 今日のタスク: 自分の open タスクのうち「期限超過 + 本日」を期限順に最大3件
 *      - ○ タップでその場完了 (S4 と同じ updateDoc)
 *      - 本文タップ / 「他N件 →」で S4 タブへ
 *   3. 今月の進捗:
 *      - 自分が KPI 対象 (当月 goals あり) → 自分のアポ/見積 達成率 + ペースバッジ
 *      - KPI 対象外 (粟村・松尾佳) → 「チーム進捗」(対象者の合算) に差し替え
 *   4. 直近営業日のチーム活動: 土日 + holidays を除いた直前の営業日の日報を
 *      メンバー別サマリーで表示。タップで S2 タイムラインへ
 *
 * クエリ設計 (全て既存ルール/インデックスの範囲内・追加不要):
 *   - config/system: getDoc
 *   - members: getDocs 全件 (4名・チーム表示の名前解決用)
 *   - goals: where yearMonth == 当月 (等価のみ)
 *   - reports 当月: where date >= 月初 AND date <= 月末 (単一フィールド範囲)
 *   - reports 直近営業日: where date == prevBizDay (等価のみ)
 *     ※ 当月内でも月またぎでも正しく取れるよう独立クエリにする
 *   - tasks: where memberId == 自分 AND status == 'open' (等価のみ・orderBy なし)
 *   - holidays: getDocs 全件 (営業日判定用)
 *
 * 更新方針: タブ表示のたびにリロード + 右上 ↻ (S4 と同じ。リスナー不使用)
 *
 * 使い方:
 *   const ctrl = mountS1Home(rootEl, {
 *     currentMember, showToast,
 *     onGoToTasks: () => {...},     // S4 タブへ
 *     onGoToTimeline: () => {...},  // S2 タブへ
 *   });
 *   ctrl.onShow({ reload: true });
 *   ctrl.destroy();
 */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-firestore.js';
import {
  getFunctions,
  httpsCallable,
} from 'https://www.gstatic.com/firebasejs/10.13.0/firebase-functions.js';
import { db } from './firebase-init.js';
import { APP_VERSION_FULL } from './version.js';

// Cloud Functions クライアント (asia-northeast1)
const functions = getFunctions(undefined, 'asia-northeast1');
const generateWeeklyVisitListFn = httpsCallable(functions, 'generateWeeklyVisitList');

// 今週の訪問リストをセッション内でキャッシュ (日をまたいだらリセット)
let _visitListCache = null; // { date, data }

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];
const TODAY_TASKS_MAX = 3;

// =====================================================================
// 日付ユーティリティ
// =====================================================================
function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function formatYearMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** "YYYY-MM-DD" → "6/15(月)" */
function formatShort(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${m}/${d}(${WEEKDAYS[dt.getDay()]})`;
}

/** 期限超過日数 (dueDate < today 前提) */
function overdueDays(dueDate, today) {
  const [y1, m1, d1] = dueDate.split('-').map(Number);
  const [y2, m2, d2] = today.split('-').map(Number);
  const ms = new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1);
  return Math.round(ms / 86400000);
}

function tsMillis(ts) {
  return ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0;
}

function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// =====================================================================
// 公開関数
// =====================================================================
export function mountS1Home(rootEl, opts) {
  const { currentMember, showToast, onGoToTasks, onGoToTimeline } = opts || {};

  const state = {
    loading: false,
    busy: false,        // タスク完了の連打防止
    destroyed: false,

    // データ
    config: null,
    members: [],        // 全メンバー (名前解決用)
    goalsMap: new Map(),// memberId → { appointmentTarget, quoteTarget }
    monthReports: [],   // 当月の全日報
    prevDayReports: [], // 直近営業日の全日報
    openTasks: [],      // 自分の open タスク
    holidaySet: new Set(), // 休業日 (YYYY-MM-DD)
    prevBizDay: null,   // 直近営業日 (YYYY-MM-DD)

    // AI 今日の訪問案
    weeklyVisit: null,       // { recommendations, summary, mode, area }
    weeklyVisitLoading: false,
    weeklyVisitError: null,
    dailyArea: '',           // 起点エリア (入力値を保持)
  };

  rootEl.innerHTML = `
    <div class="s1h-root">
      <div class="s1h-page-header">
        <div>
          <h2 class="s1h-page-title">ホーム</h2>
          <div class="s1h-page-date" id="s1h-date"></div>
        </div>
        <button type="button" class="s1h-refresh" id="s1h-refresh" aria-label="再読み込み">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"/>
          </svg>
        </button>
      </div>
      <div class="s1h-error" id="s1h-error" hidden></div>
      <div class="s1h-body" id="s1h-body">
        <div class="s1h-loading">読み込み中…</div>
      </div>
      <div class="s1h-version">チバリヨ ${APP_VERSION_FULL}</div>
    </div>
  `;

  const bodyEl = rootEl.querySelector('#s1h-body');
  const errorEl = rootEl.querySelector('#s1h-error');
  rootEl.querySelector('#s1h-refresh').addEventListener('click', () => reload());

  const dateEl = rootEl.querySelector('#s1h-date');
  const now0 = new Date();
  dateEl.textContent = `${now0.getMonth() + 1}月${now0.getDate()}日(${WEEKDAYS[now0.getDay()]})`;

  // -------------------------------------------------------------------
  // データ取得
  // -------------------------------------------------------------------
  async function fetchAll() {
    const me = currentMember.memberId;
    const now = new Date();
    const yearMonth = formatYearMonth(now);
    const monthStart = `${yearMonth}-01`;
    const monthEnd = formatDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

    // config / members / goals / holidays / 当月reports / 自分のopenタスク を並列取得
    const [cSnap, mSnap, gSnap, hSnap, rSnap, tSnap] = await Promise.all([
      getDoc(doc(db, 'config', 'system')),
      getDocs(collection(db, 'members')),
      getDocs(query(collection(db, 'goals'), where('yearMonth', '==', yearMonth))),
      getDocs(collection(db, 'holidays')),
      getDocs(query(
        collection(db, 'reports'),
        where('date', '>=', monthStart),
        where('date', '<=', monthEnd)
      )),
      getDocs(query(
        collection(db, 'tasks'),
        where('memberId', '==', me),
        where('status', '==', 'open')
      )),
    ]);

    if (!cSnap.exists()) throw new Error('config/system が存在しません');
    state.config = cSnap.data();

    state.members = mSnap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((m) => m.active !== false && m.chibariyoAccess !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    state.goalsMap = new Map();
    for (const d of gSnap.docs) {
      const g = d.data();
      state.goalsMap.set(g.memberId, {
        appointmentTarget: g.appointmentTarget || 0,
        quoteTarget: g.quoteTarget || 0,
      });
    }

    // holidays を全日展開 (月またぎの営業日判定にも使うため月で絞らない)
    state.holidaySet = new Set();
    for (const d of hSnap.docs) {
      const h = d.data();
      const s = h.startDate;
      const e = h.endDate || h.startDate;
      if (!s) continue;
      const startD = new Date(s);
      const endD = new Date(e);
      for (let d2 = new Date(startD); d2 <= endD; d2.setDate(d2.getDate() + 1)) {
        state.holidaySet.add(formatDate(d2));
      }
    }

    state.monthReports = rSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

    state.openTasks = tSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    state.openTasks.sort((a, b) => {
      const da = a.dueDate || '9999-99-99';
      const dbb = b.dueDate || '9999-99-99';
      if (da !== dbb) return da < dbb ? -1 : 1;
      return tsMillis(a.createdAt) - tsMillis(b.createdAt);
    });

    // 直近営業日 (今日より前) を求めてから、その日の日報を取得
    state.prevBizDay = computePrevBusinessDay(now);
    const pSnap = await getDocs(query(
      collection(db, 'reports'),
      where('date', '==', state.prevBizDay)
    ));
    state.prevDayReports = pSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  /** 今日より前の直近営業日 (土日 + holidays を除外、最大60日遡る) */
  function computePrevBusinessDay(now) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    for (let i = 0; i < 60; i++) {
      d.setDate(d.getDate() - 1);
      if (isBusinessDay(d)) return formatDate(d);
    }
    return formatDate(d); // フォールバック (実運用では到達しない)
  }

  function isBusinessDay(d) {
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return false;
    if (state.holidaySet.has(formatDate(d))) return false;
    return true;
  }

  async function reload() {
    if (state.loading || state.destroyed) return;
    state.loading = true;
    errorEl.hidden = true;
    try {
      await fetchAll();
      if (state.destroyed) return;
      render();
    } catch (e) {
      console.error('[S1] ホーム読み込み失敗:', e);
      errorEl.textContent = 'ホームの読み込みに失敗しました: ' + (e.message || e);
      errorEl.hidden = false;
      bodyEl.innerHTML = '';
    } finally {
      state.loading = false;
    }
  }

  // -------------------------------------------------------------------
  // KPI / ペース計算 (P1 と同一ロジック)
  // -------------------------------------------------------------------
  function computeBusinessDayProgress() {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    let elapsed = 0;
    let total = 0;
    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
      if (isBusinessDay(d)) {
        total += 1;
        if (d <= now) elapsed += 1;
      }
    }
    return { elapsed, total };
  }

  function memberActuals(memberId) {
    const my = state.monthReports.filter((r) => r.memberId === memberId);
    return {
      appointment: my.filter((r) => r.type === '訪問' && r.hasAppointment === true).length,
      quote: my.filter((r) => r.quote === true).length,
      visit: my.filter((r) => r.type === '訪問').length,
      reportCount: my.length,
      lastDate: my.length > 0 ? my.map((r) => r.date).sort().reverse()[0] : null,
    };
  }

  function computePacePts(actuals, goal, progress) {
    const expectedRatio = progress.total > 0 ? progress.elapsed / progress.total : 0;
    const apptPacePt = goal.appointmentTarget > 0
      ? ((actuals.appointment / goal.appointmentTarget) - expectedRatio) * 100
      : null;
    const quotePacePt = goal.quoteTarget > 0
      ? ((actuals.quote / goal.quoteTarget) - expectedRatio) * 100
      : null;
    let minPt = Infinity;
    if (apptPacePt !== null) minPt = Math.min(minPt, apptPacePt);
    if (quotePacePt !== null) minPt = Math.min(minPt, quotePacePt);
    if (minPt === Infinity) minPt = null;
    return { apptPacePt, quotePacePt, minPt };
  }

  function paceStatus(pt) {
    const danger = state.config.alertPacePtThreshold ?? -15;
    const warn = state.config.paceThresholdGood ?? -5;
    if (pt >= warn) return 'good';
    if (pt >= danger) return 'warn';
    return 'danger';
  }

  function paceLabel(status) {
    switch (status) {
      case 'good':   return '順調';
      case 'warn':   return '注意';
      case 'danger': return '危険';
      default:       return '—';
    }
  }

  function formatRate(actual, target) {
    if (!target || target === 0) return '—';
    return `${Math.round(actual / target * 100)}%`;
  }

  function formatPt(pt) {
    if (pt === null || pt === undefined) return '—';
    const sign = pt >= 0 ? '+' : '';
    return `${sign}${pt.toFixed(1)}pt`;
  }

  function renderProgressBar(actual, target) {
    if (!target || target === 0) {
      return '<div class="s1h-bar"><div class="s1h-bar-fill is-empty"></div></div>';
    }
    const pct = Math.min(100, Math.round(actual / target * 100));
    let cls = 'is-low';
    if (pct >= 100) cls = 'is-complete';
    else if (pct >= 75) cls = 'is-high';
    else if (pct >= 50) cls = 'is-mid';
    return `<div class="s1h-bar"><div class="s1h-bar-fill ${cls}" style="width:${pct}%"></div></div>`;
  }

  // -------------------------------------------------------------------
  // 描画
  // -------------------------------------------------------------------
  function render() {
    const today = formatDate(new Date());
    const progress = computeBusinessDayProgress();
    const isKpiTarget = state.goalsMap.has(currentMember.memberId);

    bodyEl.innerHTML = [
      renderAlerts(isKpiTarget, progress, today),
      renderWeeklyVisitList(),
      renderTodayTasks(today),
      renderProgress(isKpiTarget, progress),
      renderTeamActivity(),
    ].join('');

    bindEvents();
  }

  // ----- 0. AI 今日の訪問案 (エリア起点) -----
  function renderWeeklyVisitList() {
    // エリア入力行 (常に表示。loading/result どちらでも操作できる)
    const areaInput = `
      <div class="s1h-ai-area-row">
        <input type="text" class="s1h-ai-area-input" id="s1h-ai-area"
               placeholder="起点エリア（例: 佐賀県基山町）"
               value="${escapeHtml(state.dailyArea || '')}"
               ${state.weeklyVisitLoading ? 'disabled' : ''}>
        <button type="button" class="s1h-ai-gen-btn" data-action="gen-ai"
                ${state.weeklyVisitLoading ? 'disabled' : ''}>
          ${state.weeklyVisit ? '再提案' : '案を出す'}
        </button>
      </div>
    `;

    let body;
    if (state.weeklyVisitLoading) {
      body = `
        <div class="s1h-ai-loading">
          <div class="s1h-ai-spinner"></div>
          <span>AIが今日の訪問案を作成中…</span>
        </div>
      `;
    } else if (state.weeklyVisitError) {
      body = `
        <div class="s1h-ai-error">
          <span>取得に失敗しました</span>
          <button type="button" class="s1h-ai-retry" data-action="gen-ai">再試行</button>
        </div>
      `;
    } else if (!state.weeklyVisit || !state.weeklyVisit.recommendations.length) {
      body = `<div class="s1h-empty">起点エリアを入力して「案を出す」を押すと、AIが今日回る訪問案（5〜10件）を提案します。</div>`;
    } else {
      const { recommendations, summary, area } = state.weeklyVisit;
      const items = recommendations.map((r) => `
        <div class="s1h-ai-item ${r.isOverdue ? 'is-overdue' : ''}">
          <div class="s1h-ai-rank">${r.rank}</div>
          <div class="s1h-ai-item-body">
            <div class="s1h-ai-item-name">
              ${escapeHtml(r.clientName)}
              ${r.isOverdue ? '<span class="s1h-ai-alert-tag">途絶</span>' : ''}
            </div>
            <div class="s1h-ai-item-area">${escapeHtml(r.area)}</div>
            <div class="s1h-ai-item-reason">${escapeHtml(r.reason)}</div>
          </div>
        </div>
      `).join('');
      body = `
        <div class="s1h-ai-result-head">
          <span class="s1h-ai-week">起点: ${escapeHtml(area || '指定なし')} ・ ${recommendations.length}件</span>
          <button type="button" class="s1h-ai-close" data-action="close-ai" aria-label="閉じる">×</button>
        </div>
        ${items}
        ${summary ? `<div class="s1h-ai-summary">${escapeHtml(summary)}</div>` : ''}
      `;
    }

    return `
      <section class="s1h-section">
        <div class="s1h-section-header">
          <h3 class="s1h-section-title">今日の訪問案 <span class="s1h-ai-badge">AI</span></h3>
        </div>
        <div class="s1h-card s1h-ai-card">
          ${areaInput}
          ${body}
        </div>
      </section>
    `;
  }

  async function fetchVisitPlan() {
    state.weeklyVisitLoading = true;
    state.weeklyVisitError = null;
    render();

    try {
      const result = await generateWeeklyVisitListFn({
        mode: 'daily',
        area: state.dailyArea || '',
      });
      state.weeklyVisit = result.data;
    } catch (e) {
      console.error('[S1] AI 訪問案取得失敗:', e);
      state.weeklyVisitError = e.message || 'エラーが発生しました';
    } finally {
      state.weeklyVisitLoading = false;
      if (!state.destroyed) render();
    }
  }

  // ----- 1. アラート (該当なしなら非表示) -----
  function renderAlerts(isKpiTarget, progress, today) {
    if (!isKpiTarget) return '';  // KPI 対象外 (目標なし) はアラート対象外

    const alerts = [];
    const me = currentMember.memberId;
    const actuals = memberActuals(me);

    // 日報未入力
    const noReportDays = state.config.alertNoReportDays ?? 3;
    const now = new Date();
    const thresholdDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - noReportDays);
    const isNoReport = !actuals.lastDate || new Date(actuals.lastDate) < thresholdDate;
    if (isNoReport) {
      alerts.push(`
        <div class="s1h-alert is-warn">
          <span class="s1h-alert-icon">📝</span>
          <div class="s1h-alert-text">
            <strong>日報が${noReportDays}日以上入力されていません</strong>
            <span>最終: ${actuals.lastDate ? escapeHtml(formatShort(actuals.lastDate)) : '当月の記録なし'}</span>
          </div>
        </div>
      `);
    }

    // ペース危険 (月初は判定スキップ)
    const judgeAfter = state.config.alertPaceJudgeAfterDay ?? 15;
    if (now.getDate() >= judgeAfter) {
      const goal = state.goalsMap.get(me);
      const pace = computePacePts(actuals, goal, progress);
      const threshold = state.config.alertPacePtThreshold ?? -15;
      if (pace.minPt !== null && pace.minPt < threshold) {
        alerts.push(`
          <div class="s1h-alert is-danger">
            <span class="s1h-alert-icon">⚡</span>
            <div class="s1h-alert-text">
              <strong>目標達成ペースが遅れています</strong>
              <span>アポ: ${formatPt(pace.apptPacePt)} / 見積: ${formatPt(pace.quotePacePt)}</span>
            </div>
          </div>
        `);
      }
    }

    if (alerts.length === 0) return '';
    return `<section class="s1h-section s1h-alerts">${alerts.join('')}</section>`;
  }

  // ----- 2. 今日のタスク -----
  function renderTodayTasks(today) {
    const targets = state.openTasks.filter((t) => t.dueDate && t.dueDate <= today);
    const shown = targets.slice(0, TODAY_TASKS_MAX);
    const restCount = targets.length - shown.length;

    let inner = '';
    if (shown.length === 0) {
      inner = `<div class="s1h-empty">今日が期限のタスクはありません</div>`;
    } else {
      inner = shown.map((t) => {
        const isOverdue = t.dueDate < today;
        const dueHtml = isOverdue
          ? `<span class="s1h-task-due is-overdue">${formatShort(t.dueDate)} ・ ${overdueDays(t.dueDate, today)}日超過</span>`
          : `<span class="s1h-task-due">本日</span>`;
        const clientHtml = (t.client && t.client.clientText)
          ? `<span class="s1h-task-sep">・</span><span class="s1h-task-client">${escapeHtml(t.client.clientText)}</span>`
          : '';
        return `
          <div class="s1h-task" data-task-id="${t.id}">
            <button type="button" class="s1h-task-check" data-action="complete" aria-label="完了にする"></button>
            <div class="s1h-task-body" data-action="goto-tasks">
              <div class="s1h-task-title">${escapeHtml(t.title)}</div>
              <div class="s1h-task-meta">${dueHtml}${clientHtml}</div>
            </div>
          </div>
        `;
      }).join('');
    }

    return `
      <section class="s1h-section">
        <div class="s1h-section-header">
          <h3 class="s1h-section-title">今日のタスク</h3>
          ${targets.length > 0 ? `<span class="s1h-section-count">${targets.length}</span>` : ''}
        </div>
        <div class="s1h-card">${inner}
          ${restCount > 0 ? `
            <button type="button" class="s1h-more" data-action="goto-tasks">他 ${restCount} 件 →</button>
          ` : ''}
        </div>
      </section>
    `;
  }

  // ----- 3. 今月の進捗 (自分 or チーム) -----
  function renderProgress(isKpiTarget, progress) {
    const yearMonth = formatYearMonth(new Date());

    if (isKpiTarget) {
      const me = currentMember.memberId;
      const actuals = memberActuals(me);
      const goal = state.goalsMap.get(me);
      const pace = computePacePts(actuals, goal, progress);
      const status = pace.minPt === null ? null : paceStatus(pace.minPt);
      return renderProgressCard('今月の進捗', yearMonth, actuals, goal, status, progress);
    }

    // KPI 対象外 → チーム合算
    const kpiMemberIds = [...state.goalsMap.keys()];
    const totals = { appointment: 0, quote: 0, visit: 0, reportCount: 0 };
    const goalTotals = { appointmentTarget: 0, quoteTarget: 0 };
    for (const id of kpiMemberIds) {
      const a = memberActuals(id);
      totals.appointment += a.appointment;
      totals.quote += a.quote;
      totals.visit += a.visit;
      const g = state.goalsMap.get(id);
      goalTotals.appointmentTarget += g.appointmentTarget;
      goalTotals.quoteTarget += g.quoteTarget;
    }
    const pace = computePacePts(totals, goalTotals, progress);
    const status = pace.minPt === null ? null : paceStatus(pace.minPt);
    return renderProgressCard('チーム進捗', yearMonth, totals, goalTotals, status, progress);
  }

  function renderProgressCard(title, yearMonth, actuals, goal, status, progress) {
    return `
      <section class="s1h-section">
        <div class="s1h-section-header">
          <h3 class="s1h-section-title">${title} <span class="s1h-section-sub">(${yearMonth})</span></h3>
          ${status ? `<span class="s1h-pace-badge is-pace-${status}">${paceLabel(status)}</span>` : ''}
        </div>
        <div class="s1h-card s1h-progress-card">
          <div class="s1h-metric">
            <div class="s1h-metric-top">
              <span class="s1h-metric-label">アポ獲得</span>
              <span class="s1h-metric-value">${actuals.appointment} <span class="s1h-metric-target">/ ${goal.appointmentTarget}</span></span>
              <span class="s1h-metric-rate">${formatRate(actuals.appointment, goal.appointmentTarget)}</span>
            </div>
            ${renderProgressBar(actuals.appointment, goal.appointmentTarget)}
          </div>
          <div class="s1h-metric">
            <div class="s1h-metric-top">
              <span class="s1h-metric-label">見積提出</span>
              <span class="s1h-metric-value">${actuals.quote} <span class="s1h-metric-target">/ ${goal.quoteTarget}</span></span>
              <span class="s1h-metric-rate">${formatRate(actuals.quote, goal.quoteTarget)}</span>
            </div>
            ${renderProgressBar(actuals.quote, goal.quoteTarget)}
          </div>
          <div class="s1h-progress-footer">
            訪問 ${actuals.visit}件 ・ 営業日 ${progress.elapsed} / ${progress.total} 日
          </div>
        </div>
      </section>
    `;
  }

  // ----- 4. 直近営業日のチーム活動 -----
  function renderTeamActivity() {
    const dayLabel = formatShort(state.prevBizDay);
    const memberNames = new Map(state.members.map((m) => [m.id, m.name]));

    let inner = '';
    if (state.prevDayReports.length === 0) {
      inner = `<div class="s1h-empty">${escapeHtml(dayLabel)} の日報はありません</div>`;
    } else {
      // メンバー別にまとめる (members の order 順)
      const byMember = new Map();
      for (const r of state.prevDayReports) {
        if (!byMember.has(r.memberId)) byMember.set(r.memberId, []);
        byMember.get(r.memberId).push(r);
      }
      const orderedIds = [
        ...state.members.map((m) => m.id).filter((id) => byMember.has(id)),
        ...[...byMember.keys()].filter((id) => !memberNames.has(id)),
      ];

      inner = orderedIds.map((id) => {
        const reports = byMember.get(id);
        reports.sort((a, b) => (a.time || '').localeCompare(b.time || '') || tsMillis(a.createdAt) - tsMillis(b.createdAt));
        const name = memberNames.get(id) || (reports[0] && reports[0].memberName) || '不明';
        const first = reports[0];
        const headline = first && first.client && first.client.text ? first.client.text : '';
        const more = reports.length > 1 ? ` ほか` : '';
        return `
          <div class="s1h-activity-row" data-action="goto-timeline">
            <span class="s1h-activity-name">${escapeHtml(name)}</span>
            <span class="s1h-activity-count">${reports.length}件</span>
            <span class="s1h-activity-headline">${escapeHtml(headline)}${more}</span>
          </div>
        `;
      }).join('');
    }

    return `
      <section class="s1h-section">
        <div class="s1h-section-header">
          <h3 class="s1h-section-title">チームの動き <span class="s1h-section-sub">(${escapeHtml(dayLabel)})</span></h3>
          <button type="button" class="s1h-section-link" data-action="goto-timeline">タイムライン →</button>
        </div>
        <div class="s1h-card">${inner}</div>
      </section>
    `;
  }

  // -------------------------------------------------------------------
  // イベント
  // -------------------------------------------------------------------
  function bindEvents() {
    // 起点エリア入力を state に同期 (他操作の再描画で入力が消えないように)
    const areaEl = bodyEl.querySelector('#s1h-ai-area');
    if (areaEl) {
      areaEl.addEventListener('input', (e) => { state.dailyArea = e.target.value; });
    }

    bodyEl.querySelectorAll('[data-action]').forEach((el) => {
      el.addEventListener('click', (e) => {
        const action = el.dataset.action;
        if (action === 'complete') {
          e.stopPropagation();
          const wrap = el.closest('.s1h-task');
          if (wrap) completeTask(wrap.dataset.taskId);
        } else if (action === 'goto-tasks') {
          if (onGoToTasks) onGoToTasks();
        } else if (action === 'goto-timeline') {
          if (onGoToTimeline) onGoToTimeline();
        } else if (action === 'gen-ai') {
          // 起点エリアの入力値を取り込んでから生成
          const areaEl = bodyEl.querySelector('#s1h-ai-area');
          state.dailyArea = areaEl ? areaEl.value.trim() : '';
          state.weeklyVisit = null;
          fetchVisitPlan();
        } else if (action === 'close-ai') {
          // 出した案を閉じる (入力状態に戻す)
          state.weeklyVisit = null;
          state.weeklyVisitError = null;
          render();
        }
      });
    });
  }

  async function completeTask(taskId) {
    if (state.busy) return;
    state.busy = true;
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        status: 'done',
        completedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      if (showToast) showToast('タスクを完了しました', 'success');
      // タスクのみ再取得でも良いが、件数が少ないので全体リロードで簡潔に
      await reloadAfterMutation();
    } catch (e) {
      console.error('[S1] タスク完了失敗:', e);
      if (showToast) showToast('完了処理に失敗しました', 'error');
    } finally {
      state.busy = false;
    }
  }

  async function reloadAfterMutation() {
    try {
      await fetchAll();
      if (!state.destroyed) render();
    } catch (e) {
      console.error('[S1] 再読み込み失敗:', e);
    }
  }

  // -------------------------------------------------------------------
  // 初回ロード + 公開 API
  // -------------------------------------------------------------------
  reload();

  return {
    onShow: (o = {}) => {
      if (o.reload) reload();
    },
    destroy: () => {
      state.destroyed = true;
    },
  };
}
